import type { ApolloCache, Cache, OperationVariables } from "@apollo/client";
import {
  eventViewWarmCompleteness,
  type EventViewBaseFixtureWarmInputs,
  type EventViewFixtureWarmInputs,
  type EventViewLabelWarmInputs,
} from "@ppb/tbd-store/state/entities/sport-events/event-view-warm-selectors";
import type { AvBScore, TeamDetails } from "@ppb/tbd-store/state/entities/football-fixture/FootballFixture.types";
import { gql } from "../../types/__generated__";
import type { EventViewFixtureWarmFixtureFragment as EventViewFixtureWarmFixtureData } from "../../types/__generated__/graphql";

// Every write and the probe below select what tbd-store's `eventViewWarmCompleteness` requires;
// it stays there for the resolver's tier loop. Pinned by this file's test.

/** Which halves an urn already has. A view is backed by one fixture kind, so never both. */
export type EventViewWarmState = { label: boolean; fixture: boolean; baseFixture: boolean };

/**
 * One id for every warm read and write. Writes are **total over their fragment**: a named field that
 * `data` omits logs `Missing field '<x>' while writing result` at any depth, `__DEV__`-only — so jest
 * cannot see it and the dev-mode console check stays a manual step (trap T4 in
 * `docs/specs/event-view-apollo-header-migration.md` §13.8).
 */
function writeEventViewFragment<TData>(
  cache: ApolloCache,
  urn: string,
  // `OperationVariables`, not a second type parameter: these fragments take none.
  options: Omit<Cache.WriteFragmentOptions<TData, OperationVariables>, "id" | "from">,
): void {
  cache.writeFragment({ ...options, id: cache.identify({ __typename: "EventView", urn }) });
}

/**
 * The label half plus the football discriminator, in the same server fields `EventViewSport` and
 * `EventFixtureView` write, so the response simply overwrites it. Never in a query document, so it
 * costs no persisted-query regeneration.
 */
export const EventViewLabelWarmFragment = gql(/* GraphQL */ `
  fragment EventViewWarm on EventView {
    __typename
    urn
    sportevent {
      __typename
      urn
      name
      sport {
        __typename
        urn
        sportId
      }
    }
  }
`);

/**
 * Read-side probe: only the fields the three predicates test, so one read answers all three halves
 * instead of three reads walking three total write fragments. MUST track
 * `eventViewWarmCompleteness` — a field it stops selecting reads `undefined`, the half reports
 * unwarmed forever and the resolver reruns on every mount.
 */
export const EventViewWarmProbeFragment = gql(/* GraphQL */ `
  fragment EventViewWarmProbe on EventView {
    __typename
    urn
    sportevent {
      __typename
      urn
      name
      sport {
        __typename
        urn
        sportId
      }
    }
    fixture {
      __typename
      urn
      ... on FootballFixture {
        runnerNames {
          __typename
          home
          away
        }
        sportevent {
          __typename
          urn
        }
      }
      ... on BaseFixture {
        sportevent {
          __typename
          urn
          name
          openDate
          sport {
            __typename
            urn
            sportId
          }
        }
      }
    }
  }
`);

/**
 * Which halves this urn already has (or the server has answered). Only a *complete* half counts: a
 * partial one deadlocks the label write, and a warmed fixture stops a later sparse (tier-3) resolve
 * writing its nulls over a richer entry.
 */
export function readEventViewWarmState(cache: ApolloCache, urn: string): EventViewWarmState {
  const data = cache.readFragment({
    id: cache.identify({ __typename: "EventView", urn }),
    fragment: EventViewWarmProbeFragment,
    fragmentName: "EventViewWarmProbe",
    returnPartialData: true,
  });
  const sportevent = data?.sportevent;
  const fixture = data?.fixture;
  // By `__typename`, not `in`: both inline fragments select a `sportevent`, so a field test would
  // narrow to either and the two carry different sub-selections.
  const football = fixture?.__typename === "FootballFixture" ? fixture : undefined;
  const base = fixture?.__typename === "BaseFixture" ? fixture : undefined;
  const complete = eventViewWarmCompleteness({
    sporteventUrn: sportevent?.urn,
    sportUrn: sportevent?.sport?.urn,
    sportId: sportevent?.sport?.sportId,
    name: sportevent?.name ?? "",
    fixture: football && {
      fixtureUrn: football.urn,
      sporteventUrn: football.sportevent?.urn,
      runnerNames: football.runnerNames ?? undefined,
    },
    baseFixture: base && {
      fixtureUrn: base.urn,
      sporteventUrn: base.sportevent?.urn,
      sporteventName: base.sportevent?.name,
      // The cache holds the server's ISO string; the predicate compares the `Date` Redux holds.
      openDate: base.sportevent?.openDate ? new Date(base.sportevent.openDate) : undefined,
      sportUrn: base.sportevent?.sport?.urn,
      sportId: base.sportevent?.sport?.sportId,
    },
  });

  return {
    label: Boolean(complete.label),
    fixture: Boolean(complete.fixture),
    baseFixture: Boolean(complete.baseFixture),
  };
}

/**
 * Writes the label half. All or nothing, because each missing half is wrong on its own terms: an
 * identity-less `sportevent` replaces the normalized reference, and an empty `name` beats the later
 * server value through `labelFor`'s `??`.
 */
export function writeEventViewLabelWarm(cache: ApolloCache, urn: string, warm: EventViewLabelWarmInputs): void {
  const { label } = eventViewWarmCompleteness(warm);

  if (!label) return;

  writeEventViewFragment(cache, urn, {
    fragment: EventViewLabelWarmFragment,
    fragmentName: "EventViewWarm",
    data: {
      __typename: "EventView",
      urn,
      sportevent: {
        __typename: "SportsEvent",
        urn: label.sporteventUrn,
        name: label.name,
        sport: { __typename: "Sport", urn: label.sportUrn, sportId: label.sportId },
      },
    },
  });
}

/**
 * The scoreboard half's fixture, on the entity itself so the write can read what it already holds.
 * Same server fields `EventFixtureView` writes; `penaltyShootout` and `stats` are omitted — every
 * field is another null to write.
 */
export const EventViewFixtureWarmFixtureFragment = gql(/* GraphQL */ `
  fragment EventViewFixtureWarmFixture on FootballFixture {
    __typename
    urn
    runnerNames {
      __typename
      home
      away
    }
    isAmericanFormat
    scheduledAt
    score {
      __typename
      home
      away
    }
    firstLegScore {
      __typename
      home
      away
    }
    duration {
      __typename
      status
      period
      stoppageMinutes
      clock {
        __typename
        minute
      }
    }
    home {
      __typename
      name
      color
      crest {
        __typename
        large
      }
    }
    away {
      __typename
      name
      color
      crest {
        __typename
        large
      }
    }
    # Competition carries its urn: rich-data's FootballFixtureShell reads it, so without it the
    # warm never completes the shell and the header paints its placeholder.
    sportevent {
      __typename
      urn
      competition {
        __typename
        urn
        name
      }
    }
  }
`);

/**
 * The scoreboard half. Separate fragment and write from the label half's: the halves resolve from
 * different Redux entities and either can be missing alone.
 */
export const EventViewFixtureWarmFragment = gql(/* GraphQL */ `
  fragment EventViewFixtureWarm on EventView {
    __typename
    urn
    fixture {
      __typename
      urn
      ...EventViewFixtureWarmFixture
    }
  }
`);

/** Redux prepends `#` to the server's raw hex (`football-fixture-normalizer.ts:362`); strip it back. */
const toServerColor = (color: string | undefined) => (color ? color.replace(/^#/, "") : null);

/**
 * `FootballTeamDetails.name` is `String!`, so the unknown is `""`, not `null`. Harmless: the
 * scoreboard labels its rows from `runnerNames` first, `preferHeld` never writes it over a held team,
 * and the response overwrites it within the round trip.
 */
const toTeam = (team: TeamDetails | undefined) => ({
  __typename: "FootballTeamDetails" as const,
  name: team?.name ?? "",
  color: toServerColor(team?.color),
  crest: team?.crest ? { __typename: "Crest" as const, large: team.crest.large ?? null } : null,
});

const toScore = (score: AvBScore | undefined) =>
  score ? { __typename: "AVBScore" as const, home: score.home, away: score.away } : null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** `written`'s shape, with every leaf `held` already has taken from `held`. */
function preferHeld<T>(written: T, held: unknown): T {
  if (held == null) return written;
  if (!isRecord(written) || !isRecord(held)) return held as T;

  return Object.fromEntries(Object.entries(written).map(([key, value]) => [key, preferHeld(value, held[key])])) as T;
}

/** What the shared `SportsEvent` already holds, read before a fixture warm writes over it. */
export const EventViewWarmHeldCompetitionFragment = gql(/* GraphQL */ `
  fragment EventViewWarmHeldCompetition on SportsEvent {
    __typename
    urn
    competition {
      __typename
      urn
      name
    }
  }
`);

/**
 * Held fields, else the joined ones, else `null` (also when only one of urn/name resolves — the shells
 * read both), so a warm never nulls a held competition. Read off the `SportsEvent`, not the fixture:
 * other readers populate it before any fixture entity exists.
 */
function warmCompetition(
  cache: ApolloCache,
  sporteventUrn: string,
  joined: { competitionUrn?: string; competitionName?: string },
) {
  const held = cache.readFragment({
    id: cache.identify({ __typename: "SportsEvent", urn: sporteventUrn }),
    fragment: EventViewWarmHeldCompetitionFragment,
    fragmentName: "EventViewWarmHeldCompetition",
    returnPartialData: true,
  })?.competition;
  const urn = held?.urn ?? joined.competitionUrn;
  const name = held?.name ?? joined.competitionName;

  return urn && name ? { __typename: "Competition" as const, urn, name } : null;
}

/**
 * Writes the scoreboard half, total over its fragment — see `writeEventViewFragment`. Fills only what
 * the entity lacks: another reader (My Bets) may hold it populated before this view links it, and the
 * placeholders below would replace its teams. `false` = refused as incomplete.
 */
export function writeEventViewFixtureWarm(
  cache: ApolloCache,
  urn: string,
  warm: Partial<EventViewFixtureWarmInputs>,
): boolean {
  const { fixture: complete } = eventViewWarmCompleteness({ fixture: warm });

  if (!complete) return false;

  const held = cache.readFragment({
    id: cache.identify({ __typename: "FootballFixture", urn: complete.fixtureUrn }),
    fragment: EventViewFixtureWarmFixtureFragment,
    fragmentName: "EventViewFixtureWarmFixture",
    returnPartialData: true,
  });

  const fixture: EventViewFixtureWarmFixtureData = {
    __typename: "FootballFixture",
    urn: complete.fixtureUrn,
    runnerNames: {
      __typename: "FixtureRunnerNames",
      home: complete.runnerNames.home,
      away: complete.runnerNames.away,
    },
    // `Boolean!`, so `false` is the only writable "unknown" — it is also the non-American default,
    // and the response corrects it within the round trip.
    isAmericanFormat: complete.isAmericanFormat ?? false,
    // ISO string, not the `Date` `normalizeFootballFixtureFragmentIntoFootballFixture` built.
    scheduledAt: complete.scheduledAt?.toISOString() ?? null,
    score: toScore(complete.score),
    firstLegScore: toScore(complete.firstLegScore),
    duration: complete.duration
      ? {
          __typename: "FootballMatchDuration",
          status: complete.duration.status ?? null,
          period: complete.duration.period ?? null,
          stoppageMinutes: complete.duration.stoppageMinutes ?? null,
          clock: complete.duration.clock ? { __typename: "Clock", minute: complete.duration.clock.minute } : null,
        }
      : null,
    home: toTeam(complete.home),
    away: toTeam(complete.away),
    sportevent: {
      __typename: "SportsEvent",
      urn: complete.sporteventUrn,
      competition: warmCompetition(cache, complete.sporteventUrn, complete),
    },
  };

  writeEventViewFragment(cache, urn, {
    fragment: EventViewFixtureWarmFragment,
    fragmentName: "EventViewFixtureWarm",
    data: { __typename: "EventView", urn, fixture: preferHeld(fixture, held) },
  });

  return true;
}

/**
 * The `BaseFixture` half — the fallback header for a fixture with no score, drawn off the sportevent
 * alone. Do NOT add `mainMarket` even though `EventHeaderFixture` selects it: nothing rendered reads
 * it, and writing it replaces a populated `AssociatedMarkets` with nulls.
 */
export const EventViewBaseFixtureWarmFragment = gql(/* GraphQL */ `
  fragment EventViewBaseFixtureWarm on EventView {
    __typename
    urn
    fixture {
      __typename
      urn
      ... on BaseFixture {
        sportevent {
          __typename
          urn
          name
          openDate
          sport {
            __typename
            urn
            sportId
          }
          competition {
            __typename
            urn
            name
          }
        }
      }
    }
  }
`);

/** Writes the `BaseFixture` half, total over its fragment, so the header paints its three rows. `false` = refused. */
export function writeEventViewBaseFixtureWarm(
  cache: ApolloCache,
  urn: string,
  warm: Partial<EventViewBaseFixtureWarmInputs>,
): boolean {
  const { baseFixture: complete } = eventViewWarmCompleteness({ baseFixture: warm });

  if (!complete) return false;

  writeEventViewFragment(cache, urn, {
    fragment: EventViewBaseFixtureWarmFragment,
    fragmentName: "EventViewBaseFixtureWarm",
    data: {
      __typename: "EventView",
      urn,
      fixture: {
        __typename: "BaseFixture",
        urn: complete.fixtureUrn,
        sportevent: {
          __typename: "SportsEvent",
          urn: complete.sporteventUrn,
          name: complete.sporteventName,
          // ISO string, not the `Date` the store holds — the same conversion the scoreboard write makes.
          openDate: complete.openDate.toISOString(),
          sport: { __typename: "Sport", urn: complete.sportUrn, sportId: complete.sportId },
          competition: warmCompetition(cache, complete.sporteventUrn, complete),
        },
      },
    },
  });

  return true;
}
